import Merge from 'deepmerge';
import Cookie from '@wetransfer/concorde-cookie';

import { AppConfig } from '../core-bundle/config/app-config';

import SessionActions from './session';

import ActionHelper from '@module/redux-actions';
import Analytics from '@module/analytics';
import Router from '@module/router';
import Translations from '@module/i18n/dictionary';
import {
  HttpResponseCodes,
  ResponseHelpers,
} from '@module/http/axios/response';

import Pending from '@app/action/pending';
import Notification from '@app/action/notification';
import Transfer from '@app/action/transfer';
import Errors from '@app/action/errors';
import Account from '@app/action/account';

import API from '@app/api';
import Helper from '@app/helper';
import Transform from '@app/transformers';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';

export const actions = ActionHelper.types([
  'GET_USER',
  'CHANGE_USER_LANGUAGE',
  'CHANGE_USER_IS_VERIFIED',
  'UPDATE_USER_EMAIL',
  'REQUEST_PASSWORD_RESET_SUCCESS',
  'PASSWORD_RESET_SUCCESS',
]);

export default ActionHelper.generate({
  updateUserEmail: [actions.UPDATE_USER_EMAIL, 'email'],

  setCookie() {
    // Remember that this is a plus user
    Cookie.set('wt_plus', 1, { days: 365 });
  },

  pushSigninAnalytics(dispatch, id, rememberMe = false) {
    Analytics.addUserIdToSnowplow(id);
    dispatch(SessionActions.acceptTermsConditions()); // NOTE: User accepted this when signing up
    Analytics.pushDataLayerEvent({
      category: 'Plus',
      action: 'Signin',
      label: rememberMe,
      user: {
        user_id: id,
        account_type: 'plus',
        frequency_type: Helper.getUserFrequency(),
      },
    });
  },

  pushSignoutAnalytics() {
    Analytics.pushDataLayerEvent({
      category: 'Plus',
      action: 'Signout',
      user: {
        user_id: undefined,
        account_type: 'free',
      },
    });

    Analytics.trackStructEvent({
      category: 'plus_account',
      action: 'signout',
    });
  },

  pushSignupAnalytics(dispatch, id, billing, trk) {
    // Make sure analytics are enabled (user accepted with checkbox in form)
    if (!Analytics.isEnabled()) {
      Analytics.enable();
    }

    Analytics.pushDataLayerEvent({
      category: 'Plus',
      action: 'Signup',
      label: billing,
      user: {
        user_id: id,
        has_transacted: false,
        subscription_type: billing,
        upload_count: 0,
        download_count: 0,
        frequency_type: Helper.getUserFrequency(),
      },
      channel: trk || undefined,
    });

    this.pushSigninAnalytics(dispatch, id);
  },

  getNextPage(transfer, redirect, fallbackRoute) {
    // Users should be redirected to the download page of the transfer they were visiting
    const prevRoute = Router.getLastRoute('transfer', '/');
    if (
      transfer &&
      transfer.id &&
      transfer.items.length > 0 &&
      prevRoute &&
      prevRoute.includes('downloads')
    ) {
      const { id, recipient_id, secret } = transfer;
      const url = ['/downloads', id, recipient_id].filter(Boolean).join('/');
      // If there is no secret we want `undefined` so that the page shows Transfer expired rather than returning a 404.
      // This prevents malicious users guessing transfer urls.
      return `${url}/${secret}`;
    }
    return redirect || fallbackRoute;
  },

  redirectWithRefresh({
    session,
    route,
    account,
    nextPage,
    keepParams = true,
    keepSubDomain = true,
  }) {
    // Prevents "are you sure" browser modal from happening.
    window.onbeforeunload = null;

    const { subdomain_name, active_subscription } = account;

    const subDomain =
      active_subscription && subdomain_name && keepSubDomain
        ? subdomain_name
        : null;
    const fullDomain = subDomain
      ? `${subDomain}.${session.domain}`
      : session.domain;

    const url = new URL(
      nextPage,
      `${location.protocol}//${fullDomain}:${location.port}`
    );

    if (keepParams) {
      Object.entries(route.query).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }

    if (route.segment) {
      url.hash = route.segment;
    }
    location.assign(url.href); // Hard refresh to ensure state and tracking is fully updated
  },

  delayedResetPending(dispatch, pendingId) {
    //This ensures that submit buttons stay inactive until after the user has been redirected
    setTimeout(
      () => dispatch(Pending.resetPending(pendingId)),
      AppConfig.actions.extended_pending
    );
  },

  signin(email, password, rememberMe) {
    const pendingId = 'signin';

    return async (dispatch, getState) => {
      // Don't even request if it's empty.
      if (!email || !password) {
        return dispatch(
          Notification.setNotification('error', 'signin_no_data_exception')
        );
      }

      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.createSession({
          email,
          password,
          remember_me: rememberMe,
        });

        const { user, account } = Transform.auth(response.body);
        const { transfer, session, route } = getState();

        this.setCookie();
        this.pushSigninAnalytics(dispatch, user.id, rememberMe);
        this.delayedResetPending(dispatch, pendingId);

        const nextPage = this.getNextPage(
          transfer,
          session.redirect,
          '/transfers'
        );
        this.redirectWithRefresh({ session, route, account, nextPage });
      } catch (error) {
        if (
          ResponseHelpers.isClientError(error.status) &&
          error.body.message === 'invalid_email_or_password'
        ) {
          dispatch(Errors.setError('signin', error.body.message));
          dispatch(
            Notification.setNotification('error', 'signin_invalid_credentials')
          );
        } else {
          dispatch(Errors.setError('signin', error.body.message));
          dispatch(Notification.setNotification('error', 'signin_exception'));
        }
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  signout({ redirectTo, isForwardedTransfer } = {}) {
    const pendingId = 'signout';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.signout();
        const { session, route, account } = getState();
        // Is there a forward pending? Replace it with a normal transfer.
        if (isForwardedTransfer) {
          dispatch(Transfer.create());
        }

        this.pushSignoutAnalytics();

        // Set sign out cookie with 30 minute expiration
        const expiryDate = new Date(Date.now() + 60 * 30 * 1000);
        Cookie.set(AppConfig.cookies.signout.name, 1, { expires: expiryDate });

        this.delayedResetPending(dispatch, pendingId);

        const nextPage = redirectTo
          ? redirectTo
          : Router.getLastRoute('transfer', '/');

        this.redirectWithRefresh({
          session,
          route,
          account,
          nextPage,
          keepSubDomain: false,
        });
      } catch (error) {
        dispatch(Notification.setNotification('error', 'signout_exception'));
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  signup(params = {}, tracking = {}, formName = 'signup', subscriptionId) {
    const pendingId = formName;

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      // Remove vat if it only has the prefix
      let data = { ...params };
      if (data.vat_number.length <= 2) {
        data.vat_number = '';
      }

      // Include tracking params
      if (tracking) {
        data = Merge(data, tracking);
      }

      try {
        const response =
          formName === 'upgrade'
            ? await API.Account.upgradeSubscription(data, {
                pathExtValues: { subscriptionId },
              })
            : await API.User.signup(data);

        const { checkout: { order } = {} } = response.body;
        const { user, account } = Transform.auth(response.body.user);
        const { session, route } = getState();

        if (response.body.order_completed && response.body.redirect_to_url) {
          // A real redirect, since the session will be replaced.
          const url = new URL(response.body.redirect_to_url);
          location.assign(url.href);
        }

        //Initialise record of sent and downloaded transfers
        Cookie.set('wt_sent', 0, { days: 365 });
        Cookie.set('wt_downloaded', 0, { days: 365 });

        this.setCookie();
        this.pushSignupAnalytics(dispatch, user.id, params.billing, data.trk);
        this.delayedResetPending(dispatch, pendingId);

        // A payment might not be required if the order total is zero
        if (order && order.skip_payment === true) {
          this.redirectWithRefresh({
            session,
            route,
            account,
            nextPage: '/payment/pending?skip=true',
            keepParams: false,
          });
          return;
        }

        this.redirectWithRefresh({
          session,
          route,
          account,
          nextPage: '/payment/methods',
        });
      } catch (error) {
        if (error && error.body && error.body.fields) {
          dispatch(Errors.setError('signup', error.body.fields));
        }
        dispatch(Notification.setNotification('error', 'signup_account_error'));
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  getUser() {
    const pendingId = 'getUser';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.getCurrentUser();

        dispatch({
          type: actions.GET_USER,
          details: response.body,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_user_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  updateUser(params = {}) {
    const pendingId = 'account';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const data = { ...params };
        const response = await API.User.updateUser(data);

        dispatch({
          type: actions.GET_USER,
          details: response.body,
        });
        dispatch(Notification.setNotification('notification', 'update_user'));
        dispatch(Pending.resetPending(pendingId));
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          dispatch(Errors.setError('account', error.body.fields));
          dispatch(
            Notification.setNotification('error', 'something_went_wrong')
          );
        } else {
          dispatch(Notification.setNotification('error', 'update_user_error'));
        }
        dispatch(Pending.resetPending(pendingId));
        throw error;
      }
    };
  },

  updateUserLanguage(language) {
    const pendingId = 'updateUserLanguage';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.updateUserLanguage(
          { language },
          {
            pathExtValues: {
              language,
            },
          }
        );

        dispatch({
          type: actions.CHANGE_USER_LANGUAGE,
          language,
        });

        // Update the dictionary
        Translations.update(language, response.body);
      } catch (error) {
        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  updatePassword(params = {}) {
    const pendingId = 'password';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.updatePassword(params);

        dispatch(
          Notification.setNotification('notification', 'update_password')
        );
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          dispatch(Errors.setError('password', error.body.fields));
        } else {
          dispatch(
            Notification.setNotification('error', 'update_password_exception')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  requestPasswordReset(email) {
    const pendingId = 'requestPasswordReset';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.requestPasswordReset({
          email,
        });

        dispatch({
          type: actions.REQUEST_PASSWORD_RESET_SUCCESS,
          details: response.body,
        });
      } catch (error) {
        if (error.status === HttpResponseCodes.NOT_FOUND) {
          dispatch(Errors.setError('requestPasswordReset', error.body.message));
          dispatch(
            Notification.setNotification(
              'error',
              'request_password_reset_no_user'
            )
          );
        } else {
          dispatch(
            Errors.setError(
              'requestPasswordReset',
              'request_password_reset_exception'
            )
          );
          dispatch(
            Notification.setNotification(
              'error',
              'request_password_reset_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  resetPassword(params = {}) {
    const pendingId = 'password';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.User.resetPassword(params);

        dispatch({
          type: actions.PASSWORD_RESET_SUCCESS,
          details: response.body,
        });

        const { user, account } = Transform.auth(response.body);
        const { transfer, session, route } = getState();

        this.setCookie();
        this.pushSigninAnalytics(dispatch, user.id);
        this.delayedResetPending(dispatch, pendingId);

        const nextPage = this.getNextPage(
          transfer,
          session.redirect,
          '/transfers'
        );
        this.redirectWithRefresh({ session, route, account, nextPage });
      } catch (error) {
        if (error.status === HttpResponseCodes.BAD_REQUEST) {
          if (error.body.fields && error.body.fields.reset_password_token) {
            dispatch(
              Notification.setNotification(
                'error',
                'reset_password_token_invalid'
              )
            );
          } else {
            dispatch(
              Notification.setNotification('error', 'reset_password_error')
            );
          }
        } else {
          dispatch(Errors.setError('password', 'reset_password_exception'));
          dispatch(
            Notification.setNotification('error', 'reset_password_exception')
          );
        }
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  unsubscribeOnboarding(verifierId) {
    const pendingId = 'unsubscribeOnboarding';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.unsubscribeUserOnboarding({ verifier_id: verifierId });
      } finally {
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  unsubscribeLinkTransfer(verifierId) {
    const pendingId = 'unsubscribeLinkTransfer';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        await API.User.unsubscribeLinkTransfer({ verifier_id: verifierId });
      } finally {
        dispatch(Pending.resetPending(pendingId));
      }
    };
  },

  verifyAccountEmail(verificationCode) {
    return async (dispatch) => {
      await API.User.verifyAccountEmail({
        verification_code: verificationCode,
      });
      dispatch(Transfer.setState(TRANSFER_STATES.VERIFY_ACCOUNT_SUCCESS));
      dispatch({
        type: actions.CHANGE_USER_IS_VERIFIED,
        isVerified: true,
      });
    };
  },

  resendVerifyAccountEmail(email) {
    return async (dispatch) => {
      await API.User.validate({ email });
      await API.User.resendVerifyAccountEmail({ email });
      dispatch(this.updateUserEmail(email));
      dispatch(Account.updateAccountMembershipUserEmail(email));
      dispatch(Transfer.setState(TRANSFER_STATES.VERIFY_ACCOUNT));
    };
  },
});
